import type { ReactElement, SVGProps } from 'react'
import { Area, Bar, CartesianGrid, ComposedChart, ReferenceLine, XAxis, YAxis } from 'recharts'
import { EmptyState } from '@/components/patterns/empty-state'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { Typography } from '@/components/ui/typography'
import { useIsMobile } from '@/hooks/use-mobile'
import type {
  SalesSummaryReportGranularity,
  SalesSummaryReportTimeSeriesBucket,
} from '@/lib/services/reports/reports.types'
import { cn, parseCurrency } from '@/lib/utils'
import {
  type BucketedChartAxisDomains,
  type ChartAxisTick,
  type CombinedChartAxis,
  type SalesStatisticsBucket,
  buildBucketedChartAxisDomains,
  buildCombinedChartAxis,
  formatBucketAxisLabel,
  formatBucketTooltipLabel,
  getAreaChartTickInterval,
  getChartAxisTick,
} from './-helpers'

const CUMULATIVE_SALES_KEY = 'cumulativeSales'
const BUCKETED_SALES_KEY = 'bucketedSales'
const POSITIVE_BUCKETED_PREFIX = 'positive_'
const NEGATIVE_BUCKETED_PREFIX = 'negative_'
const BUCKETED_SALES_COLOR = 'var(--color-chart-2)'
const CUMULATIVE_AXIS_WIDTH = 56
const CUMULATIVE_AXIS_WIDTH_MOBILE = 42
const AXIS_LEGEND_HEIGHT = 28

/** A time-series amount the chart can plot: money, or the paid order count. */
type SalesPerformanceSeriesKey = keyof Pick<
  SalesSummaryReportTimeSeriesBucket,
  'total_sales' | 'net_sales' | 'average_ticket' | 'paid_order_count'
>

type AxisDomain = CombinedChartAxis['domain']

type SalesPerformanceChartProps = {
  data: SalesSummaryReportTimeSeriesBucket[]
  dataKey: SalesPerformanceSeriesKey
  granularity: SalesSummaryReportGranularity
  timezone: string
  axisFormatter: (value: number) => string
  valueFormatter: (value: number) => string
  bucketedLabel: string
  cumulativeLabel: string
  emptyStateLabel?: string
}

export function SalesPerformanceChart({
  data,
  dataKey,
  granularity,
  timezone,
  axisFormatter,
  valueFormatter,
  bucketedLabel,
  cumulativeLabel,
  emptyStateLabel = 'No chart data available.',
}: SalesPerformanceChartProps) {
  const isMobile = useIsMobile()
  const chartData = buildRunningTotalData(data, dataKey)
  const runningTotalKey = getRunningTotalKey(dataKey)
  const positiveBucketedKey = getPositiveBucketedKey(dataKey)
  const negativeBucketedKey = getNegativeBucketedKey(dataKey)
  const combinedAxis = buildCombinedChartAxis(chartData, runningTotalKey, dataKey)
  const bucketedAxisDomains = buildBucketedChartAxisDomains(chartData, dataKey, combinedAxis.domain)
  const chartConfig: ChartConfig = {
    [CUMULATIVE_SALES_KEY]: {
      label: cumulativeLabel,
      color: 'var(--color-chart-1)',
    },
    [BUCKETED_SALES_KEY]: {
      label: bucketedLabel,
      color: BUCKETED_SALES_COLOR,
    },
  }

  if (!chartData.length) {
    return (
      <NoChartState className="h-full min-h-[220px] sm:min-h-[280px]" label={emptyStateLabel} />
    )
  }

  return (
    <div className="relative h-[220px] w-full max-w-full overflow-hidden sm:h-[280px] xl:h-full">
      <ManualYAxisLabels
        axis={combinedAxis}
        formatter={axisFormatter}
        getTickValue={(tick) =>
          getBucketedAxisValueAtCombinedTick(tick, combinedAxis.domain, bucketedAxisDomains)
        }
        isMobile={isMobile}
        legendColor={BUCKETED_SALES_COLOR}
        legendLabel={bucketedLabel}
      />
      <ManualYAxisLabels
        axis={combinedAxis}
        formatter={axisFormatter}
        isMobile={isMobile}
        legendColor="var(--color-chart-1)"
        legendLabel={cumulativeLabel}
        side="right"
      />
      <ChartContainer
        className="h-full w-full max-w-full overflow-hidden aspect-auto"
        config={chartConfig}
      >
        <ComposedChart
          data={chartData}
          margin={{
            left: isMobile ? 0 : 4,
            right: isMobile ? CUMULATIVE_AXIS_WIDTH_MOBILE : CUMULATIVE_AXIS_WIDTH,
            top: AXIS_LEGEND_HEIGHT,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id={`fill-${CUMULATIVE_SALES_KEY}`} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={`var(--color-${CUMULATIVE_SALES_KEY})`}
                stopOpacity={0.28}
              />
              <stop
                offset="95%"
                stopColor={`var(--color-${CUMULATIVE_SALES_KEY})`}
                stopOpacity={0.03}
              />
            </linearGradient>
          </defs>
          <CartesianGrid
            horizontal={ChartHorizontalGridLine}
            vertical={false}
            yAxisId="cumulative"
          />
          <XAxis
            dataKey="bucket_start"
            tickLine={false}
            axisLine={false}
            tick={getChartAxisTick(isMobile)}
            minTickGap={isMobile ? 12 : 24}
            tickMargin={isMobile ? 6 : 8}
            interval={getAreaChartTickInterval(chartData.length, granularity, isMobile)}
            // The x-axis plots each bucket's bucket_start.
            tickFormatter={(value: string) => formatBucketAxisLabel(value, granularity, timezone)}
          />
          <YAxis
            yAxisId="cumulative"
            dataKey={runningTotalKey}
            domain={combinedAxis.domain}
            ticks={combinedAxis.ticks}
            tick={getHiddenAxisTick(isMobile)}
            tickLine={false}
            axisLine={false}
            tickFormatter={axisFormatter}
            width={isMobile ? CUMULATIVE_AXIS_WIDTH_MOBILE : CUMULATIVE_AXIS_WIDTH}
            allowDecimals={!isDiscreteCountSeries(dataKey)}
          />
          <YAxis yAxisId="bucketedPositive" hide domain={bucketedAxisDomains.positive} />
          <YAxis yAxisId="bucketedNegative" hide domain={bucketedAxisDomains.negative} />
          <ReferenceLine
            yAxisId="cumulative"
            y={0}
            stroke="var(--color-border)"
            strokeOpacity={0.85}
          />
          <ChartTooltip
            cursor={false}
            filterNull
            content={
              <ChartTooltipContent
                labelFormatter={(_label, payload) =>
                  formatBucketTooltipLabel(
                    readTooltipBucket(payload[0]?.payload),
                    granularity,
                    timezone
                  )
                }
                formatter={(value, name, item) => (
                  <SalesPerformanceTooltipItem
                    color={item.color}
                    label={name === CUMULATIVE_SALES_KEY ? cumulativeLabel : bucketedLabel}
                    value={valueFormatter(Number(value ?? 0))}
                  />
                )}
              />
            }
          />
          <Bar
            yAxisId="bucketedPositive"
            name={BUCKETED_SALES_KEY}
            dataKey={positiveBucketedKey}
            // Sign-split layers share one x slot while keeping independent y-axis scales.
            stackId={BUCKETED_SALES_KEY}
            fill={`var(--color-${BUCKETED_SALES_KEY})`}
            fillOpacity={0.86}
            radius={[5, 5, 0, 0]}
            isAnimationActive={false}
          />
          <Bar
            yAxisId="bucketedNegative"
            name={BUCKETED_SALES_KEY}
            dataKey={negativeBucketedKey}
            stackId={BUCKETED_SALES_KEY}
            fill={`var(--color-${BUCKETED_SALES_KEY})`}
            fillOpacity={0.86}
            // Recharts draws negative vertical bars with a negative height, so this rounds the end away from zero.
            radius={[5, 5, 0, 0]}
            isAnimationActive={false}
          />
          <Area
            yAxisId="cumulative"
            name={CUMULATIVE_SALES_KEY}
            type="monotone"
            dataKey={runningTotalKey}
            stroke={`var(--color-${CUMULATIVE_SALES_KEY})`}
            fill={`url(#fill-${CUMULATIVE_SALES_KEY})`}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ChartContainer>
    </div>
  )
}

function buildRunningTotalData(
  data: SalesSummaryReportTimeSeriesBucket[],
  dataKey: SalesPerformanceSeriesKey
) {
  const runningTotalKey = getRunningTotalKey(dataKey)
  const positiveBucketedKey = getPositiveBucketedKey(dataKey)
  const negativeBucketedKey = getNegativeBucketedKey(dataKey)
  let runningValue = 0

  return data.map((row) => {
    // API money arrives as decimal strings; the chart and its axes plot parsed amounts.
    const bucketedValue = parseCurrency(row[dataKey])
    runningValue += bucketedValue

    return {
      ...row,
      [dataKey]: bucketedValue,
      [runningTotalKey]: runningValue,
      [positiveBucketedKey]: bucketedValue >= 0 ? bucketedValue : null,
      [negativeBucketedKey]: bucketedValue < 0 ? bucketedValue : null,
    }
  })
}

function getRunningTotalKey(dataKey: SalesPerformanceSeriesKey) {
  return `running_${dataKey}`
}

function getPositiveBucketedKey(dataKey: SalesPerformanceSeriesKey) {
  return `${POSITIVE_BUCKETED_PREFIX}${dataKey}`
}

function getNegativeBucketedKey(dataKey: SalesPerformanceSeriesKey) {
  return `${NEGATIVE_BUCKETED_PREFIX}${dataKey}`
}

// Recharts types a tooltip item's data row as `any`; read back the bucket bounds it carries.
function readTooltipBucket(row: unknown): SalesStatisticsBucket | null {
  if (typeof row !== 'object' || row === null) {
    return null
  }

  return {
    bucket_start: 'bucket_start' in row ? readBucketValue(row.bucket_start) : null,
    bucket_end: 'bucket_end' in row ? readBucketValue(row.bucket_end) : null,
  }
}

function readBucketValue(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function getBucketedAxisValueAtCombinedTick(
  tick: number,
  combinedDomain: AxisDomain,
  bucketedAxisDomains: BucketedChartAxisDomains
) {
  const topRatio = getAxisTopRatio(tick, combinedDomain)
  const zeroTopRatio = getAxisTopRatio(0, combinedDomain)
  const bucketedDomain =
    topRatio <= zeroTopRatio ? bucketedAxisDomains.positive : bucketedAxisDomains.negative
  const value = getAxisValueAtTopRatio(topRatio, bucketedDomain)

  return Math.abs(value) < 0.000001 ? 0 : value
}

function getAxisTopRatio(value: number, domain: AxisDomain) {
  const [domainMin, domainMax] = domain
  const range = domainMax - domainMin || 1

  return (domainMax - value) / range
}

function getAxisValueAtTopRatio(topRatio: number, domain: AxisDomain) {
  const [domainMin, domainMax] = domain
  const range = domainMax - domainMin || 1

  return domainMax - topRatio * range
}

function isDiscreteCountSeries(dataKey: SalesPerformanceSeriesKey) {
  return dataKey === 'paid_order_count'
}

function getHiddenAxisTick(isMobile: boolean): ChartAxisTick & { opacity: number } {
  return {
    ...getChartAxisTick(isMobile),
    opacity: 0,
  }
}

function SalesPerformanceTooltipItem({
  color,
  label,
  value,
}: {
  color: string | undefined
  label: string
  value: string
}) {
  return (
    <div className="flex w-full items-center justify-between gap-3">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} aria-hidden />
        {label}
      </span>
      <span className="font-medium tabular-nums text-foreground">{value}</span>
    </div>
  )
}

/** The props Recharts gives a custom CartesianGrid line that this one reads. */
type ChartGridLineProps = Pick<
  SVGProps<SVGLineElement>,
  'className' | 'stroke' | 'strokeDasharray' | 'strokeOpacity' | 'strokeWidth'
> & {
  offset: { top: number; height: number }
  x1: number
  x2: number
  y1: number
  y2: number
}

function ChartHorizontalGridLine({
  className,
  offset,
  stroke,
  strokeDasharray,
  strokeOpacity,
  strokeWidth,
  x1,
  x2,
  y1,
  y2,
}: ChartGridLineProps): ReactElement<SVGElement> {
  const chartBottom = offset.top + offset.height

  // Recharts requires an element for every grid line; an empty group draws nothing.
  if (Number.isFinite(y1) && Number.isFinite(chartBottom) && Math.abs(y1 - chartBottom) < 0.5) {
    return <g />
  }

  return (
    <line
      className={className}
      x1={x1}
      x2={x2}
      y1={y1}
      y2={y2}
      fill="none"
      stroke={stroke}
      strokeDasharray={strokeDasharray}
      strokeOpacity={strokeOpacity}
      strokeWidth={strokeWidth}
    />
  )
}

type ManualYAxisLabelsProps = {
  axis: CombinedChartAxis
  formatter: (value: number) => string
  getTickValue?: (tick: number) => number
  isMobile: boolean
  legendColor: string
  legendLabel: string
  side?: 'left' | 'right'
}

function ManualYAxisLabels({
  axis,
  formatter,
  getTickValue = (tick) => tick,
  isMobile,
  legendColor,
  legendLabel,
  side = 'left',
}: ManualYAxisLabelsProps) {
  const [domainMin, domainMax] = axis.domain
  const range = domainMax - domainMin || 1
  const axisWidth = isMobile ? CUMULATIVE_AXIS_WIDTH_MOBILE : CUMULATIVE_AXIS_WIDTH
  const isRightSide = side === 'right'

  return (
    <div
      className={cn(
        'pointer-events-none absolute top-0 bottom-7 z-10',
        isRightSide ? 'right-0 text-right' : 'left-0 text-left'
      )}
      style={{ width: axisWidth }}
      aria-hidden="true"
    >
      <div
        className={cn(
          'absolute top-0 flex h-5 w-max items-center gap-1.5 whitespace-nowrap text-[11px] font-medium text-muted-foreground',
          isRightSide ? 'right-0 justify-end' : 'left-0 justify-start'
        )}
      >
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: legendColor }} />
        <span>{legendLabel}</span>
      </div>
      <div className="absolute right-0 bottom-0 left-0" style={{ top: AXIS_LEGEND_HEIGHT }}>
        {axis.ticks.map((tick) => {
          const top = ((domainMax - tick) / range) * 100
          const value = getTickValue(tick)

          return (
            <span
              key={tick}
              className={cn(
                'absolute top-0 text-[11px] font-medium tabular-nums text-muted-foreground',
                isRightSide ? 'right-0 text-right' : 'right-2 text-right'
              )}
              style={{
                top: `${top}%`,
                transform: `translateY(${top === 0 ? '0' : top === 100 ? '-100%' : '-50%'})`,
              }}
            >
              {formatter(value)}
            </span>
          )
        })}
      </div>
    </div>
  )
}

function NoChartState({ className, label }: { className: string; label: string }) {
  return <EmptyState size="compact" surface="transparent" className={className} title={label} />
}
